import React, { useEffect, useState, useMemo } from 'react'
import classNames from 'classnames'
import { useStores } from 'hooks/useStores'
import { observer } from 'mobx-react'
import AuthDisplayWrapper from 'components/AuthDisplayWrapper'
import routes from 'routes'
import AppMenuItem from './components/AppMenuItem'
import { adminSidebarItems, SidebarItem, sidebarItems } from './constants'
import { EFeatureFlags } from 'constants/featureFlags'
import styles from './styles.module.scss'

interface ISideBarContentProps {
  isCondensed: boolean
  isBasicUser: boolean
  haveEditorRole: boolean
}
const SideBarContent = (props: ISideBarContentProps) => {
  const { isCondensed, isBasicUser, haveEditorRole } = props
  const { notificationStore, authStore, iconBuilderStore, commonLibraryStore, organizationStore } =
    useStores()
  const { userDetail } = authStore
  const { organization } = organizationStore
  const {
    isThemeSetting,
    isModularProcess,
    isReportTool,
    isCollectionFeature,
    isStandardUserCanAccessUser,
    isWorkPlanEnabled,
    isPriorityUpdateEnabled
  } = organization || {
    isCollectionFeature: true,
    isModularProcess: true,
    isReportTool: true,
    isThemeSetting: true,
    isStandardUserCanAccessUser: false,
    isWorkPlanEnabled: undefined,
    isPriorityUpdateEnabled: undefined
  }
  const [sidebar, setSidebar] = useState<SidebarItem[]>([])

  useEffect(() => {
    notificationStore.aggregateCountNotifications()
    iconBuilderStore.fetchCMSIconList()
  }, [])

  useEffect(() => {
    if (isBasicUser) {
      setSidebar(sidebarItems)
      return
    }

    setSidebar(adminSidebarItems)
  }, [isBasicUser])

  const filteredSidebarItems = useMemo(() => {
    return sidebar.map((item, index) => {
      const filteredItem = { ...item }
      
      // Apply children filtering based on organization settings
      if (!isThemeSetting && item?.path === routes.setting.value) {
        filteredItem.children = item.children?.filter(child => child.path !== routes.setting.appearance.value)
      }

      if (
        (!isReportTool || (isBasicUser && !userDetail?.isReportTool)) &&
        item?.children?.some(child => child.path === routes.report.organization.value)
      ) {
        filteredItem.children = item.children?.filter(child => child.path !== routes.report.organization.value)
      }

      if (!isModularProcess && item?.children?.some(child => child.path === routes.commonLibrary.value)) {
        filteredItem.children = item.children?.filter(child => child.path !== routes.commonLibrary.value)
      }

      if (
        isBasicUser &&
        !isStandardUserCanAccessUser &&
        item?.children?.some(child => child.path === routes.users.value)
      ) {
        filteredItem.children = item.children?.filter(child => child.path !== routes.users.value)
      }

      // Filter children by allowedRoles (hide items not permitted for current role)
      if (filteredItem.children && userDetail?.authRole) {
        filteredItem.children = filteredItem.children.filter(
          child => !child.allowedRoles || child.allowedRoles.includes(userDetail.authRole)
        )
      } else if (filteredItem.children && !userDetail?.authRole) {
        filteredItem.children = filteredItem.children.filter(child => !child.allowedRoles)
      }

      // Apply item-level filtering
      // Org setting: hide Work Plans entries when disabled
      if (item?.featureFlag === EFeatureFlags.WORK_PLANS && isWorkPlanEnabled === false) {
        return null
      }

      if (filteredItem.children) {
        filteredItem.children = filteredItem.children.filter(child => {
          if (child.featureFlag === EFeatureFlags.WORK_PLANS && isWorkPlanEnabled === false) return false
          return true
        })
      }
      if (item?.path === routes.processes.value && !haveEditorRole && isBasicUser) {
        return null
      }

      if (item?.path === routes.archive.value && !haveEditorRole && isBasicUser) {
        return null
      }

      if (item?.path === routes.commonLibrary.value && !haveEditorRole && isBasicUser) {
        return null
      }

      if (item?.path === routes.commonLibrary.value && !commonLibraryStore?.isFeatureEnabled) {
        return null
      }

      if (item?.path === routes.commonLibrary.value && !isModularProcess && isBasicUser) {
        return null
      }

      if (
        item?.path === routes.report.organization.value &&
        (!isReportTool || (isBasicUser && !userDetail?.isReportTool))
      ) {
        return null
      }

      if (item?.path === routes.collections.value && !isCollectionFeature) {
        return null
      }

      if (item?.path === routes.users.value && !isStandardUserCanAccessUser && isBasicUser) {
        return null
      }

      // Create the menu item component
      const child = item.allowedRoles ? (
        <AuthDisplayWrapper allowedRoles={item.allowedRoles} key={`${item.path}-${index}-auth`}>
          <AppMenuItem item={filteredItem} index={index} isCondensed={isCondensed} isBasicUser={isBasicUser} />
        </AuthDisplayWrapper>
      ) : (
        <AppMenuItem
          item={filteredItem}
          index={index}
          key={`${item.path}-${index}`}
          isCondensed={isCondensed}
          isBasicUser={isBasicUser}
        />
      )

      return child
    }).filter(Boolean) // Remove null items
  }, [
    sidebar,
    organization,
    userDetail,
    isBasicUser,
    haveEditorRole,
    commonLibraryStore?.isFeatureEnabled,
    isCondensed,
    isWorkPlanEnabled,
    isPriorityUpdateEnabled
  ])

  return (
    <React.Fragment>
      <div className={classNames('topbar-nav', styles.appMenu)}>
        <ul className="metismenu side-nav" id="menu-bar">
          {filteredSidebarItems}
        </ul>
      </div>
    </React.Fragment>
  )
}

export default observer(SideBarContent)
